import React, { useState, useEffect } from 'react';
import { SlideToggle } from 'ui/SlideToggle';
import FluidButton from 'ui/FluidButton';
import Checkbox from 'ui/Checkbox';
import { useModal } from 'hooks/useModal';
import { TextInput } from 'ui/TextInput';
import { Multiselect } from 'ui/Multiselect';
import { CategoryDropdown } from 'ui/CategoryDropdown/CategoryDropdown';
import { HeroImageUploadModal } from './HeroImageUploadModal';
import { ContentImage } from './ContentImage';
import { makeBackendApi } from 'services/BackendApi';
import { IValueLabelPair } from 'interfaces';
import { useCategories, useProductTypes } from '../hooks/useContentMarketingQueries';
import { IMarketingManagerProductType } from 'services/MarketingManagerApi';
import { ICrmActivityCategoryGroup } from 'services/CrmApi/types/CrmActivityTypes';
import { ExistingDirectoryEntriesSimpleSearch } from 'containers/CRM/components/ExistingDirectoryEntriesSearch/ExistingDirectoryEntriesSimpleSearch';
import { ECrmDirectoryEntryType } from 'services/CrmApi/types/DirectoryEntry';

interface IValidationErrors {
  title?: boolean;
  crmCompanyUuid?: boolean;
  categoryUuids?: boolean;
  shortDescriptionTitle?: boolean;
  shortDescriptionContent?: boolean;
  heroImage?: boolean;
}

interface IMainInfoSectionProps {
  contentEditState: any;
  validationErrors: IValidationErrors;
  isCreateMode?: boolean;
  updateTitle: (title: string) => void;
  updateCrmCompanyUuid: (crmCompanyUuid: string | null) => void;
  updateCategoryUuid: (categoryUuids: string[]) => void;
  updateCategoryEnabled: (enabled: boolean) => void;
  updateDestination: (destinations: string[]) => void;
  updateProductName: (productName: string) => void;
  updateIsExclusive: (isExclusive: boolean) => void;
  updateProductTypeUuids: (productTypeUuids: string[]) => void;
  addHeroImage: (file: File, displayName: string) => Promise<void>;
}

export const MainInfoSection = ({
  contentEditState,
  validationErrors,
  isCreateMode = false,
  updateTitle,
  updateCrmCompanyUuid,
  updateCategoryUuid,
  updateCategoryEnabled,
  updateDestination,
  updateProductName,
  updateIsExclusive,
  updateProductTypeUuids,
  addHeroImage,
}: IMainInfoSectionProps) => {
  const [destinationOptions, setDestinationOptions] = useState<IValueLabelPair[]>([]);
  const heroUploadModalOps = useModal();

  // Fetch categories and product types from API
  const { data: categoriesData, isLoading: isLoadingCategories } = useCategories();
  const { data: productTypesData, isLoading: isLoadingProductTypes } = useProductTypes();

  // Load countries when component mounts
  useEffect(() => {
    const loadCountries = async () => {
      try {
        const backendApi = makeBackendApi();
        const response = await backendApi.getNamesSearch('');
        const data = response.data.data;

        // Get just the countries
        const options: IValueLabelPair[] = [];

        if (data.countries) {
          data.countries.forEach(country => {
            options.push({
              value: country.name,
              label: country.name,
            });
          });
        }

        setDestinationOptions(options);
      } catch (error) {
        console.error('Error loading countries:', error);
      }
    };

    loadCountries();
  }, []);

  // Transform categories data for Multiselect component
  const categoryOptions: IValueLabelPair[] = (categoriesData || []).map(category => ({
    value: category.uuid,
    label: category.value,
  }));

  // Transform hierarchical product types data to CategoryDropdown format
  const transformProductTypesToCategoryGroups = (
    productTypes: IMarketingManagerProductType[]
  ): ICrmActivityCategoryGroup[] => {
    // Filter enabled product types
    const enabledProductTypes = productTypes.filter(pt => pt.enabled);

    // Get parent categories (those with parentId = null and have children)
    const parentCategories = enabledProductTypes.filter(
      pt => pt.parentId === null && pt.children && pt.children.length > 0
    );

    return parentCategories.map(parent => ({
      id: parent.uuid,
      name: parent.value,
      categories: (parent.children || [])
        .filter(child => child.enabled)
        .map(child => ({
          id: child.uuid,
          name: child.value,
        })),
    }));
  };

  const productTypeCategoryGroups = transformProductTypesToCategoryGroups(productTypesData || []);

  return (
    <div className="space-y-[10px]">
      {/* section title */}
      <span className="flex items-center justify-between w-full pr-4">
        <span className="font-bold text-[17px] text-gray-800 font-pt-sans">Main Info</span>
      </span>

      {/* Hero Photo Section */}
      <div className="w-full" data-validation-id="heroImage">
        <div
          className={`h-[220px] rounded flex flex-col items-center justify-center gap-2.5 relative overflow-hidden ${
            contentEditState.heroImage
              ? ''
              : validationErrors.heroImage
              ? 'bg-red-25 border-2 border-red-95 border-dashed p-4'
              : 'bg-brown-prime-10 border border-gray-40 border-dashed p-4'
          }`}
        >
          {isCreateMode ? (
            // Show disabled message when in create mode
            <div className="text-center flex flex-col items-center">
              <p className="text-gray-600 text-sm mb-2 font-pt-sans">
                Please save the content item before adding a hero image
              </p>
              <FluidButton
                type="secondary"
                disabled={true}
                className="bg-gray-100 border border-gray-300 text-gray-500 cursor-not-allowed"
              >
                Add Hero Photo
              </FluidButton>
            </div>
          ) : contentEditState.heroImage ? (
            <>
              {/* Hero Image Display */}
              <ContentImage
                upload={contentEditState.heroImage}
                index={0}
                className="absolute inset-0 w-full h-full"
                imgClassName="object-cover"
              />
              {/* Overlay Button */}
              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40">
                <FluidButton
                  type="secondary"
                  onClick={() => heroUploadModalOps.showModal()}
                  className="bg-white border border-brown-prime text-brown-prime hover:bg-brown-prime hover:text-white"
                >
                  Change Hero Photo
                </FluidButton>
              </div>
            </>
          ) : (
            <FluidButton
              type="secondary"
              onClick={() => heroUploadModalOps.showModal()}
              className="bg-white border border-brown-prime text-brown-prime hover:bg-brown-prime hover:text-white"
            >
              Add Hero Photo
            </FluidButton>
          )}
        </div>
        {validationErrors.heroImage && !isCreateMode && (
          <p className="text-red-95 text-13px leading-2xs font-pt-sans mt-5px mb-0">Hero image is required</p>
        )}
      </div>

      {/* Hero Image Upload Modal - only show in edit mode */}
      {!isCreateMode && (
        <HeroImageUploadModal
          isOpen={heroUploadModalOps.isOpen}
          onClose={heroUploadModalOps.handleClose}
          onUpload={addHeroImage}
        />
      )}

      {/* row 1 - Title - Full Width */}
      <div className="w-full" data-validation-id="title">
        <label className="block text-[13px] font-pt-sans text-gray-700 mb-1 tracking-wider">Title *</label>
        <TextInput
          id="content-title"
          value={contentEditState.title || ''}
          onChange={e => updateTitle(e.currentTarget.value)}
          placeholder="Content title"
          errorMessage={validationErrors.title ? 'Field is required' : undefined}
        />
      </div>

      {/* Row 2: CRM Company & Category & Destination */}
      <div className="grid grid-cols-1 gap-2 md:grid-cols-3 md:gap-10">
        {/* CRM Company */}
        <div data-validation-id="crmCompanyUuid">
          <label className="block text-[13px] font-pt-sans text-gray-700 mb-1 tracking-wider">CRM Company *</label>
          <div className={validationErrors.crmCompanyUuid ? 'border border-red-95 bg-red-25 rounded' : ''}>
            <ExistingDirectoryEntriesSimpleSearch
              value={contentEditState.crmCompanyUuid || ''}
              onChange={companyUuid => {
                updateCrmCompanyUuid(companyUuid);
              }}
              directoryEntryType={ECrmDirectoryEntryType.COMPANY}
              className={validationErrors.crmCompanyUuid ? 'bg-red-25' : ''}
            />
          </div>
          {validationErrors.crmCompanyUuid && (
            <p className="text-red-95 text-13px leading-2xs font-pt-sans mt-5px mb-0">Field is required</p>
          )}
        </div>

        {/* Category */}
        <div className="flex-1" data-validation-id="categoryUuids">
          <label className="block text-[13px] font-pt-sans text-gray-700 mb-1 tracking-wider">Category *</label>
          <div className="flex gap-2.5">
            <div className="flex-1 max-w-[85%]">
              <div className={validationErrors.categoryUuids ? 'border border-red-95 bg-red-25 rounded' : ''}>
                <Multiselect
                  className="bg-ivory w-full"
                  itemsClassname="bg-ivory"
                  primaryCtaClassName={validationErrors.categoryUuids ? 'bg-red-25' : ''}
                  itemCtaClassName="hover:bg-gray-10"
                  onUpdate={sv => {
                    updateCategoryUuid(sv);
                  }}
                  options={categoryOptions}
                  selectedValues={contentEditState.categoryUuids || []}
                  placeholderText={isLoadingCategories ? 'Loading categories...' : 'Select categories'}
                  disabled={isLoadingCategories}
                />
              </div>
              {validationErrors.categoryUuids && (
                <p className="text-red-95 text-13px leading-2xs font-pt-sans mt-5px mb-0">Field is required</p>
              )}
            </div>
            <div className="flex items-center self-start mt-[6px]">
              <SlideToggle
                id="categories-enabled"
                checked={!contentEditState.categoriesHidden}
                onActivate={() => updateCategoryEnabled(true)}
                onDeactivate={() => updateCategoryEnabled(false)}
                labelClassName={contentEditState.categoriesHidden ? 'bg-gray-20' : 'bg-teal-40'}
                buttonClassName={contentEditState.categoriesHidden ? 'bg-gray-100' : 'bg-teal-100'}
              />
            </div>
          </div>
        </div>

        {/* Destination */}
        <div className="flex-1">
          <label className="block text-[13px] font-pt-sans text-gray-700 mb-1 tracking-wider">Destination</label>
          <Multiselect
            className="bg-ivory w-full"
            itemsClassname="bg-ivory"
            itemCtaClassName="hover:bg-gray-10"
            onUpdate={sv => {
              updateDestination(sv);
            }}
            options={destinationOptions}
            selectedValues={contentEditState.destinations || []}
          />
        </div>
      </div>

      {/* Row 3: Product Name & Product Type */}
      <div className="grid grid-cols-1 gap-2 md:grid-cols-3 md:gap-10">
        {/* Product Name (1 column) */}
        <div>
          <label className="block text-[13px] font-pt-sans text-gray-700 tracking-wider">Product Name</label>
          <TextInput
            id="product-name"
            value={contentEditState.productName || ''}
            onChange={e => updateProductName(e.currentTarget.value)}
            placeholder="Product Name Sample"
            className="bg-ivory placeholder:normal-case"
          />
        </div>

        {/* Product Type (2 columns) */}
        <div className="col-span-2">
          <label className="block text-[13px] font-pt-sans text-gray-700 mb-1 tracking-wider">Product Type</label>
          {isLoadingProductTypes ? (
            <div className="bg-ivory w-full p-2 text-gray-600">Loading product types...</div>
          ) : (
            <CategoryDropdown
              optionGroups={productTypeCategoryGroups}
              selectedOptions={contentEditState.productTypeUuids || []}
              onSelectedOptionsChange={updateProductTypeUuids}
              innerTextInputClassName="bg-ivory"
              noOptionsMessage="No product types found"
            />
          )}
        </div>
      </div>

      {/* Exclusive Checkbox */}
      <label className="flex items-center gap-2">
        <Checkbox
          checked={contentEditState.isExclusive || false}
          onChange={e => updateIsExclusive((e.target as HTMLInputElement).checked)}
          label="Exclusive"
          className="text-[16px] font-pt-sans"
        />
        <span className="font-pt-sans text-black text-[16px]">Exclusive</span>
      </label>
    </div>
  );
};
